import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Virtuoso } from 'react-virtuoso'
import { DeleteModal } from '@/components/commons/delete-modal'
import { InputText } from '@/components/commons/input-text'
import { SearchLgIcon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Skeleton } from '@/components/ui/skeleton'
import { useSubgeoColorLegend } from '@/features/subgeo/hooks/use-subgeo-colors'
import { useDeleteSubgeo } from '@/features/subgeo/queries/use-delete-subgeo'
import { useSubgeoStore } from '@/features/subgeo/store/subgeo-store'
import { debounce } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import { SubgeoCreateForm } from './create-subgeo-form'
import { SubgeoEditForm } from './edit-subgeo-form'
import { SubgeoListItem } from './subgeo-list-item'
import { SubgeoListItemSkeleton } from './subgeo-list-item-skeleton'

interface SubgeoListProps {
	isLoading?: boolean
}

export const SubgeoList = ({ isLoading = false }: SubgeoListProps) => {
	const { t } = useTranslation()
	const {
		subgeos,
		selectedNeighborhoods,
		editingSubgeoId,
		startEditingSubgeo,
		cancelEditingSubgeo
	} = useSubgeoStore()
	const [search, setSearch] = useState('')
	const [deletingSubgeoId, setDeletingSubgeoId] = useState<string | null>(
		null
	)

	const legendItems = useSubgeoColorLegend(subgeos)

	const deletingSubgeo = subgeos.find((s) => s.id === deletingSubgeoId)

	const { mutate: deleteSubgeo, isPending: isDeleting } = useDeleteSubgeo(
		{ id: deletingSubgeoId || '' },
		{
			onSuccess: () => {
				toast.success(t('subgeo:delete_subgeo_success'))
				setDeletingSubgeoId(null)
			}
		}
	)

	const handleDeleteClick = (subgeoId: string) => {
		setDeletingSubgeoId(subgeoId)
	}

	const handleDeleteConfirm = () => {
		deleteSubgeo()
	}

	const handleDeleteCancel = () => {
		setDeletingSubgeoId(null)
	}

	const onSearch = debounce((value: string) => {
		setSearch(value)
	}, 350)

	const filteredSubgeos = useMemo(() => {
		if (!search) {
			return legendItems
		}
		const searchLower = search.toLowerCase()
		return legendItems.filter((s) =>
			s.name.toLowerCase().includes(searchLower)
		)
	}, [legendItems, search])

	const handleCancelEdit = () => {
		cancelEditingSubgeo()
		setSearch('')
	}

	const showCreateForm = selectedNeighborhoods.size > 0 && !editingSubgeoId

	if (showCreateForm) {
		return (
			<div className='flex h-[calc(100dvh-80px)] w-full flex-col gap-2'>
				<SubgeoCreateForm />
			</div>
		)
	}

	if (editingSubgeoId) {
		const editingSubgeo = subgeos.find((s) => s.id === editingSubgeoId)
		if (editingSubgeo) {
			return (
				<div className='flex h-[calc(100dvh-80px)] w-full flex-col gap-2'>
					<SubgeoEditForm
						key={editingSubgeo.id}
						onCancel={handleCancelEdit}
						subgeo={editingSubgeo}
					/>
				</div>
			)
		}
	}

	return (
		<>
			<div className='flex min-h-0 w-full flex-1 flex-col gap-2'>
				<div className='flex w-full flex-col items-start gap-2 px-2.5 pt-2.5'>
					<h6 className='font-medium text-neutral-500 text-sm'>
						{isLoading ? (
							<div className='flex items-center justify-between gap-2'>
								<Skeleton height={20} width={20} />
								<Skeleton height={20} width={80} />
							</div>
						) : (
							t('subgeo:subgeos_count', { count: subgeos.length })
						)}
					</h6>
					<InputText
						inputSize='sm'
						onChange={(e) => onSearch(e.target.value)}
						placeholder={t('subgeo:search_subgeos')}
						type='text'
						typeIcon={<SearchLgIcon size={16} />}
					/>
					{!isLoading && filteredSubgeos.length === 0 && (
						<InputHelpText helpText={t('subgeo:no_subgeos')} />
					)}
				</div>

				{isLoading ? (
					<SubgeoListItemSkeleton count={8} />
				) : (
					<div className='min-h-0 w-full flex-1 overflow-hidden px-2.5 pb-2.5'>
						<Virtuoso
							className='[&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:hover:bg-neutral-400 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-neutral-50'
							data={filteredSubgeos}
							itemContent={(_, item) => {
								const subgeo = subgeos.find(
									(s) => s.id === item.id
								)
								if (!subgeo) {
									return null
								}

								return (
									<SubgeoListItem
										item={item}
										key={item.id}
										onDelete={() =>
											handleDeleteClick(item.id)
										}
										onEdit={() =>
											startEditingSubgeo(item.id)
										}
									/>
								)
							}}
							overscan={100}
							totalCount={filteredSubgeos.length}
						/>
					</div>
				)}
			</div>

			{deletingSubgeo && (
				<DeleteModal
					isLoading={isDeleting}
					onClose={handleDeleteCancel}
					onDelete={handleDeleteConfirm}
					paragraph={t('subgeo:delete_subgeo_description')}
					title={t('subgeo:delete_subgeo_title', {
						name: deletingSubgeo.name
					})}
				/>
			)}
		</>
	)
}

export default SubgeoList
